/** Brightness/Contrast adjustment — pure math + types, no React, no Fabric.
 *
 *  Achromatic (one LUT applied to R/G/B identically), so much
 *  simpler than Curves/Levels. Two parameters:
 *
 *  - `brightness` (-100..100): tone-aware blend toward white (positive)
 *    or black (negative). Tapers near the endpoints rather than
 *    clipping — `+100` doesn't turn the image solid white, just
 *    pulls every pixel ~halfway to white.
 *  - `contrast` (-100..100): pivots around mid-grey (128) using
 *    Photoshop's classic factor formula. `+100` gives a sharp
 *    S-curve; `-100` flattens everything toward grey. */

export interface BrightnessContrastValue {
  brightness: number;
  contrast: number;
}

export const identityBrightnessContrastValue = (): BrightnessContrastValue => ({
  brightness: 0,
  contrast: 0,
});

/** Build the 256-entry achromatic LUT.
 *
 *  Brightness: for `b ∈ [0, 1]` blends toward white via
 *    `out = in + b · (255 − in)` — tapers as `in` approaches 255.
 *  For `b ∈ [-1, 0]` blends toward black via `out = in + b · in`.
 *  Both branches keep the endpoints (0 and 255) clamped to themselves
 *  in their *opposite* direction (positive brightness leaves 255
 *  unchanged; negative brightness leaves 0 unchanged).
 *
 *  Contrast: classic Photoshop factor
 *    factor = (259 · (c + 255)) / (255 · (259 − c))   where c ∈ [-255, 255]
 *  applied as a pivot around 128:  out = 128 + factor · (in − 128). */
export function buildBrightnessContrastLUT(value: BrightnessContrastValue): Uint8ClampedArray {
  const lut = new Uint8ClampedArray(256);
  const b = value.brightness / 100; // -1..1
  const c = (value.contrast / 100) * 255; // -255..255
  const factor = (259 * (c + 255)) / (255 * (259 - c));
  for (let i = 0; i < 256; i++) {
    let v = i;
    if (b > 0) v = v + b * (255 - v);
    else if (b < 0) v = v + b * v;
    v = 128 + factor * (v - 128);
    lut[i] = Math.round(Math.max(0, Math.min(255, v)));
  }
  return lut;
}

/** Pure pixel transform — returns a fresh ImageData. Tests use
 *  this variant; the canvas-bound wrapper does the putImageData. */
export function applyBrightnessContrastPixels(
  source: ImageData,
  value: BrightnessContrastValue,
): ImageData {
  const w = source.width;
  const h = source.height;
  const lut = buildBrightnessContrastLUT(value);
  const sd = source.data;
  const out = new Uint8ClampedArray(sd.length);
  for (let i = 0; i < sd.length; i += 4) {
    out[i] = lut[sd[i]];
    out[i + 1] = lut[sd[i + 1]];
    out[i + 2] = lut[sd[i + 2]];
    out[i + 3] = sd[i + 3];
  }
  return new ImageData(out, w, h);
}

export function applyBrightnessContrastToCanvas(
  source: ImageData,
  target: HTMLCanvasElement,
  value: BrightnessContrastValue,
): void {
  const tCtx = target.getContext('2d');
  if (!tCtx) return;
  if (target.width !== source.width || target.height !== source.height) {
    target.width = source.width;
    target.height = source.height;
  }
  tCtx.putImageData(applyBrightnessContrastPixels(source, value), 0, 0);
}
